import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Gift, Mail, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExitIntentPopup = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [loading, setLoading] = useState(false);

  // Don't show popup on admin routes or immersive experiences
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isImmersiveRoute = location.pathname === '/ship';
  const isPurchaseRoute = location.pathname === '/cart' || location.pathname.startsWith('/checkout') || location.pathname === '/gift-finder';
  const isAuthRoute = ['/login', '/register', '/forgot-password', '/reset-password'].some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    // Don't show on admin pages, immersive 3D experiences, or auth pages
    if (isAdminRoute || isImmersiveRoute || isAuthRoute) return;
    
    // Check if already shown this session
    const shown = sessionStorage.getItem('exitPopupShown');
    if (shown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves from top of page
      if (e.clientY <= 0 && !hasShown && !window.__selahPopupOpen) {
        window.__selahPopupOpen = true;
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Also show after 30 seconds of inactivity
    const inactivityTimer = setTimeout(() => {
      if (!hasShown && !sessionStorage.getItem('exitPopupShown') && !window.__selahPopupOpen) {
        window.__selahPopupOpen = true;
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    }, 30000);

    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(inactivityTimer);
    };
  }, [hasShown, isAdminRoute, isImmersiveRoute, isAuthRoute]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;

    setLoading(true);
    try {
      // Send to A.I_SHA Email List backend
      const response = await fetch(`${API_URL}/api/aisha/email-list/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          source: 'exit_intent',
          tags: ['exit_intent', 'discount_seeker', 'faith15']
        })
      });
      
      const data = await response.json();
      
      // Track with Klaviyo if available
      if (typeof window !== 'undefined' && window._learnq) {
        window._learnq.push(['identify', { '$email': email }]);
        window._learnq.push(['track', 'Exit Intent Signup', { discount: 'FAITH15' }]);
      }

      setSubmitted(true);
      
      // Copy discount code to clipboard
      try {
        await navigator.clipboard.writeText('FAITH15');
      } catch (err) {
        // Clipboard API not available in this browser
      }
    } catch (error) {
      logger.error('Subscription error:', error);
      // Still show success - don't block UX
      setSubmitted(true);
    }
    setLoading(false);
  };

  const handleClose = () => {
    window.__selahPopupOpen = false;
    setIsVisible(false);
  };

  if (!isVisible || isPurchaseRoute || isAuthRoute) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
      data-testid="exit-intent-popup"
    >
      <div 
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10"
          data-testid="exit-popup-close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6 text-white text-center">
          <Gift className="w-12 h-12 mx-auto mb-3 animate-bounce" />
          <h2 className="text-2xl font-bold">Wait! Don&apos;t Leave Empty-Handed</h2>
        </div>

        {/* Content */}
        <div className="p-6">
          {!submitted ? (
            <>
              <p className="text-center text-slate-600 mb-4">
                Get <span className="font-bold text-amber-600 text-xl">15% OFF</span> your first order!
              </p>
              
              <div className="bg-amber-50 border-2 border-dashed border-amber-300 rounded-lg p-4 text-center mb-4">
                <p className="text-sm text-slate-500 mb-1">Your exclusive code:</p>
                <p className="text-2xl font-bold text-amber-600 tracking-widest">FAITH15</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1"
                    required
                    data-testid="exit-popup-email"
                  />
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600">
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
                <p className="text-xs text-center text-slate-400">
                  Join our faith family & get exclusive deals
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">You&apos;re In!</h3>
              <p className="text-slate-600 mb-4">
                Your code <span className="font-bold text-amber-600">FAITH15</span> has been copied!
              </p>
              <Button onClick={handleClose} className="bg-amber-500 hover:bg-amber-600">
                Start Shopping
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-3 text-center">
          <p className="text-xs text-slate-400">
            ALL ABOARD SELAH - Faith-Inspired Fashion
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
